import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Plus, Code2, Save, Trash2, Loader2, Copy, CheckCircle2, X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/misc';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { apiFetch } from '@/lib/api';
import { useProjects } from '@/components/ProjectSwitcher';
import { type Project } from '@/lib/projects';
import { useT } from '@/lib/i18n';

interface WidgetKey {
  id: string;
  project_id: string;
  name: string;
  public_key: string;
  allowed_origins: string[];
  active: boolean;
  created_at?: string;
  last_used_at?: string;
}

const DRAFT: WidgetKey = {
  id: '', project_id: '', name: '', public_key: '', allowed_origins: [''], active: true,
};

export function Widgets() {
  const t = useT();
  const qc = useQueryClient();
  const { data: projects = [] } = useProjects();
  const [selId, setSelId] = useState<string | null>(null);
  const [draft, setDraft] = useState<WidgetKey | null>(null);

  const { data: widgets = [], isLoading } = useQuery({
    queryKey: ['widgets'],
    queryFn: async (): Promise<WidgetKey[]> => ((await apiFetch('GET', '/me/widgets')).data as { data?: WidgetKey[] })?.data ?? [],
  });

  const selected = draft ?? widgets.find((w) => w.id === selId) ?? null;
  const refresh = () => qc.invalidateQueries({ queryKey: ['widgets'] });

  const newWidget = () => {
    setDraft({ ...DRAFT, project_id: projects[0]?.id ?? '' });
    setSelId(null);
  };

  const projectName = (id: string) => projects.find((p) => p.id === id)?.name ?? id;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">{t('widgets.subtitle')}</p>
        <Button className="w-full shrink-0 sm:w-auto" onClick={newWidget}><Plus className="size-4" /> {t('widgets.new')}</Button>
      </div>

      <div className="grid gap-6 md:grid-cols-[20rem_1fr]">
        {/* Lista */}
        <Card className="h-fit">
          <CardContent className="flex flex-col gap-1 p-2">
            {isLoading ? (
              [0, 1].map((i) => <Skeleton key={i} className="h-12 w-full" />)
            ) : widgets.length === 0 && !draft ? (
              <div className="flex flex-col items-center gap-2 py-10 text-center">
                <Code2 className="size-7 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">{t('widgets.empty')}</p>
                <Button size="sm" onClick={newWidget}><Plus className="size-4" /> {t('widgets.createFirst')}</Button>
              </div>
            ) : (
              <>
                {draft && (
                  <div className="flex items-center gap-2 rounded-md bg-accent px-2 py-2 text-sm">
                    <span className="size-2 rounded-full bg-amber-400" />
                    <span className="flex-1 truncate text-muted-foreground italic">{t('widgets.newDraft')}</span>
                  </div>
                )}
                {widgets.map((w) => (
                  <button
                    key={w.id}
                    onClick={() => { setDraft(null); setSelId(w.id); }}
                    className={'flex items-center gap-2.5 rounded-md px-2 py-2 text-left text-sm transition-colors ' + (!draft && w.id === selId ? 'bg-accent' : 'hover:bg-accent/50')}
                  >
                    <span className={'size-2 shrink-0 rounded-full ' + (w.active ? 'bg-emerald-500' : 'bg-muted-foreground/40')} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium">{w.name || t('widgets.untitled')}</p>
                      <p className="truncate text-xs text-muted-foreground">{projectName(w.project_id)}</p>
                    </div>
                  </button>
                ))}
              </>
            )}
          </CardContent>
        </Card>

        {/* Editor */}
        {selected ? (
          <WidgetEditor
            key={selected.id || 'draft'}
            widget={selected}
            projects={projects}
            onSaved={(id) => { setDraft(null); setSelId(id); refresh(); }}
            onDeleted={() => { setDraft(null); setSelId(null); refresh(); }}
            onCancelDraft={() => setDraft(null)}
          />
        ) : (
          <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">{t('widgets.selectHint')}</CardContent></Card>
        )}
      </div>
    </div>
  );
}

function WidgetEditor({
  widget, projects, onSaved, onDeleted, onCancelDraft,
}: {
  widget: WidgetKey;
  projects: Project[];
  onSaved: (id: string) => void;
  onDeleted: () => void;
  onCancelDraft: () => void;
}) {
  const t = useT();
  const isNew = !widget.id;

  const [name, setName] = useState(widget.name);
  const [projectId, setProjectId] = useState(widget.project_id);
  const [origins, setOrigins] = useState<string[]>(widget.allowed_origins.length ? widget.allowed_origins : ['']);
  const [active, setActive] = useState(widget.active);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const cleanOrigins = () => origins.map((o) => o.trim()).filter(Boolean);

  const save = async () => {
    if (!name.trim()) { toast.error(t('widgets.nameRequired')); return; }
    if (isNew && !projectId) { toast.error(t('widgets.projectRequired')); return; }
    setSaving(true);
    try {
      const r = isNew
        ? await apiFetch('POST', '/me/widgets', { name: name.trim(), project_id: projectId, allowed_origins: cleanOrigins() })
        : await apiFetch('PATCH', `/me/widgets/${widget.id}`, { name: name.trim(), allowed_origins: cleanOrigins(), active });
      if (!r.ok) { toast.error(t('widgets.saveFailed')); return; }
      const data = r.data as WidgetKey;
      toast.success(isNew ? t('widgets.created') : t('widgets.saved'));
      onSaved(data.id);
    } finally { setSaving(false); }
  };

  const del = async () => {
    if (!window.confirm(t('widgets.confirmDelete', { name: widget.name }))) return;
    setDeleting(true);
    try {
      const r = await apiFetch('DELETE', `/me/widgets/${widget.id}`);
      if (r.ok) { toast.success(t('widgets.revoked')); onDeleted(); }
      else toast.error(t('widgets.revokeFailed'));
    } finally { setDeleting(false); }
  };

  const setOrigin = (i: number, v: string) => setOrigins((o) => o.map((x, idx) => (idx === i ? v : x)));
  const addOrigin = () => setOrigins((o) => [...o, '']);
  const removeOrigin = (i: number) => setOrigins((o) => (o.length <= 1 ? [''] : o.filter((_, idx) => idx !== i)));

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardContent className="flex flex-col gap-5 p-5">
          <div>
            <h2 className="text-base font-semibold">{isNew ? t('widgets.newTitle') : t('widgets.editTitle')}</h2>
            <p className="text-sm text-muted-foreground">{t('widgets.editSubtitle')}</p>
          </div>

          {/* public_key (só para os salvos) */}
          {!isNew && (
            <div className="flex flex-col gap-1.5">
              <Label>{t('widgets.publicKey')}</Label>
              <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2">
                <code className="flex-1 truncate font-mono text-sm">{widget.public_key}</code>
                <CopyButton text={widget.public_key} variant="ghost" />
              </div>
              <p className="text-xs text-muted-foreground">{t('widgets.publicKeyHint')}</p>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label>{t('widgets.name')}</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('widgets.namePlaceholder')} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>{t('widgets.project')}</Label>
              {isNew ? (
                <Select value={projectId} onValueChange={setProjectId}>
                  <SelectTrigger><SelectValue placeholder={t('widgets.projectPlaceholder')} /></SelectTrigger>
                  <SelectContent>
                    {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Input value={projects.find((p) => p.id === projectId)?.name ?? projectId} disabled />
              )}
            </div>
          </div>

          {/* Origens permitidas (CORS) */}
          <div className="flex flex-col gap-2">
            <Label>{t('widgets.origins')}</Label>
            <p className="text-xs text-muted-foreground">{t('widgets.originsHint')}</p>
            <div className="flex flex-col gap-2">
              {origins.map((o, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input value={o} onChange={(e) => setOrigin(i, e.target.value)} placeholder="https://app.cliente.com" className="font-mono" />
                  <Button type="button" variant="ghost" size="icon" onClick={() => removeOrigin(i)} aria-label={t('widgets.removeOrigin')}>
                    <X className="size-4" />
                  </Button>
                </div>
              ))}
            </div>
            <Button type="button" variant="outline" size="sm" className="self-start" onClick={addOrigin}>
              <Plus className="size-4" /> {t('widgets.addOrigin')}
            </Button>
          </div>

          {/* Ativo (só para os salvos) */}
          {!isNew && (
            <div className="flex flex-col gap-1.5">
              <Label>{t('widgets.status')}</Label>
              <label className="flex h-9 cursor-pointer items-center gap-2 text-sm">
                <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} className="size-4 accent-primary" />
                {active ? t('widgets.statusActive') : t('widgets.statusPaused')}
              </label>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-4">
            <div className="flex gap-2">
              {isNew && <Button variant="ghost" size="sm" onClick={onCancelDraft}>{t('common.cancel')}</Button>}
            </div>
            <div className="flex gap-2">
              {!isNew && <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" disabled={deleting} onClick={del}>{deleting ? <Loader2 className="size-4 animate-spin" /> : <Trash2 className="size-4" />} {t('widgets.revoke')}</Button>}
              <Button disabled={saving} onClick={save}>{saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />} {isNew ? t('common.create') : t('common.save')}</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Snippet de instalação (só para os salvos) */}
      {!isNew && <SnippetCard publicKey={widget.public_key} />}
    </div>
  );
}

function SnippetCard({ publicKey }: { publicKey: string }) {
  const t = useT();
  const [mode, setMode] = useState<'floating' | 'embedded'>('floating');

  const floating = `<script src="https://widget.bzapper.com.br/v1/widget.js" data-key="${publicKey}" async></script>`;
  const embedded = `<script src="https://widget.bzapper.com.br/v1/widget.js" async></script>\n<bzapper-widget data-key="${publicKey}"></bzapper-widget>`;
  const snippet = mode === 'floating' ? floating : embedded;

  return (
    <Card>
      <CardContent className="flex flex-col gap-4 p-5">
        <div>
          <h2 className="text-base font-semibold">{t('widgets.installTitle')}</h2>
          <p className="text-sm text-muted-foreground">{t('widgets.installSubtitle')}</p>
        </div>

        <div className="flex gap-2">
          <Button size="sm" variant={mode === 'floating' ? 'default' : 'outline'} onClick={() => setMode('floating')}>{t('widgets.floating')}</Button>
          <Button size="sm" variant={mode === 'embedded' ? 'default' : 'outline'} onClick={() => setMode('embedded')}>{t('widgets.embedded')}</Button>
        </div>
        <p className="text-xs text-muted-foreground">{mode === 'floating' ? t('widgets.floatingHint') : t('widgets.embeddedHint')}</p>

        <div className="flex items-start gap-2">
          <pre className="flex-1 overflow-x-auto rounded-md border bg-muted/40 p-3 text-xs leading-relaxed"><code>{snippet}</code></pre>
          <CopyButton text={snippet} variant="outline" />
        </div>

        <div className="flex flex-col gap-1 text-xs text-muted-foreground">
          <p className="font-medium text-foreground">{t('widgets.attrsTitle')}</p>
          <p><code>data-key</code> — {t('widgets.attrKey')}</p>
          <p><code>data-api</code> — {t('widgets.attrApi')} <code>https://api.bzapper.com.br</code></p>
          <p><code>data-position</code> — {t('widgets.attrPosition')} <code>bottom-right</code> | <code>bottom-left</code></p>
        </div>
      </CardContent>
    </Card>
  );
}

function CopyButton({ text, variant }: { text: string; variant: 'ghost' | 'outline' }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      type="button"
      variant={variant}
      size={variant === 'ghost' ? 'icon' : 'sm'}
      onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
    >
      {copied ? <CheckCircle2 className="size-4 text-emerald-600" /> : <Copy className="size-4" />}
    </Button>
  );
}
